import React, { ChangeEvent, FunctionComponent, useCallback, useMemo, useState } from "react";
import {
  BannerInline,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Modal,
  Paper,
  Radio,
  RadioGroup,
  Search,
  SegmentedButton,
  SegmentedButtonGroup,
  Step,
  Stepper,
  Tag,
  Typography
} from "@hxnova/react-components";
import { Icon } from "@hxnova/icons";
import { NovaTheme } from "@hxnova/themes";
import { ThemeProvider } from "@hxnova/react-components";
import _ from "underscore";

import "../../../css/dialogs/export_deviations_ifc_wizard.css";
import getMostRecentScanDatesByFloorId from "../../services/getters/scan_dataset_getters/get_most_recent_scan_dates_by_floor_id";
import useAvvirSelector from "../../services/component_helpers/component_effects/use_avvir_selector";
import useFloorsForProject from "../../queries/floors/use_floors_for_project";
import useScanDatasetFilesForProject from "../../queries/scan_datasets/use_scan_dataset_files_for_project";
import useScanDatasetsForProject from "../../queries/scan_datasets/use_scan_datasets_for_project";
import useTrades from "../../queries/trades/use_trades";
import TradeTreeRows from "../shared/trade_tree_rows";
import { filterTradeNodesByQuery, leafCodesByNode } from "../../services/utilities/trade_tree";
import { buildBreakdownFromList, convertBreakdownToFilter } from "../../services/utilities/breakdown";
import { toggleElement, toggleElementCascade, updateFilterFromIdList } from "../../services/utilities/filter";
import {
  asBuiltNoBuiltTradesText,
  asBuiltToleranceAffirmationText,
  asBuiltToleranceNoDeviationsText,
  DEFAULT_DEVIATION_TOLERANCE_OPTION,
  DEVIATION_TOLERANCE_OPTIONS,
  DeviationToleranceOption,
  isDefaultTolerance,
  toleranceAffirmationText,
  toleranceDisplayLabel, toleranceFileNameSlug, toleranceNoDeviationsText,
  toleranceOptionByKey,
  toleranceOptionToMetres,
  toleranceSummaryText
} from "../../services/utilities/ifc_export/deviation_tolerance_options";
import {
  exportTypeFileNameToken,
  generateIfcFileName,
  generateZipFileName,
  scanScopeFileNameToken,
  tradesFileNameSegment
} from "../../services/utilities/ifc_export/ifc_export_file_names";
import type { IfcExportFileNamingLabels } from "../../services/utilities/ifc_export/ifc_export_file_names";
import type { DeviationsIfcType } from "./export_deviations_ifc_dialog";
import type { StringIdFilter } from "../../services/utilities/filter";
import type { BreakdownNode } from "../../services/utilities/breakdown";
import type { ApiTrade } from "avvir";
import useFloorBuiltElementCounts from "../../queries/projects/use_floor_built_element_counts";
import useFloorTradeBuiltElementCounts from "../../queries/projects/use_floor_trade_built_element_counts";
import useFloorTradeDeviationMagnitudes from "../../queries/projects/use_floor_trade_deviation_magnitudes";
import { useFlags } from "../../services/utilities/launch_darkly_helper";
import useCurrentScanFloorTradeDeviationMagnitudes from "../../queries/projects/use_current_scan_floor_trade_deviation_magnitudes";
import DateConverter from "../../services/converters/date_converter";

const INCHES_TO_METERS_RATIO = 39.37;
const SMALLEST_DEVIATION_IN_METERS = 0.125 / INCHES_TO_METERS_RATIO;

/**
 * Which export this configures. As-built skips the scan step and treats the
 * tolerance as a placement threshold rather than an inclusion filter.
 */
export type IfcWizardVariant = "deviations" | "asBuilt";

/** The pipeline `type`, which the deviation export's scan step picks. */
export type ExportIfcType = DeviationsIfcType | "as_built";

export type Props = {
  open: boolean
  projectId: string
  projectName?: string
  /** Which export to configure. Defaults to the deviation export. */
  variant?: IfcWizardVariant
  /**
   * Whether to include the trade-selection step. Defaults to true; the nav bar gates this on the
   * `ifcExportTradeFilter` flag so the trade filter can stay hidden while the tolerance flow ships.
   */
  tradeFilterEnabled?: boolean
  onClose: () => void
  /**
   * Triggers the export; resolves once the job is submitted, rejects if submission fails.
   * `minimumDeviationMetres` is undefined for the default tolerance; `tradeCodes` are the
   * selected leaf trade codes to filter on; `fileNaming` carries the filename tokens the pipeline
   * cannot derive on its own, so the exported files match the preview shown here.
   */
  onExport: (ifcType: ExportIfcType, floorIds: string[], minimumDeviationMetres: number | undefined, tradeCodes: string[], fileNaming: IfcExportFileNamingLabels) => Promise<unknown>
}

type WizardStep = "scan" | "area" | "tolerance" | "trade" | "review";
type ExportState = "idle" | "loading" | "error";

/** Selected leaf trade codes read off a cascade selection (parents carry -1/1 aggregates). */
const selectedLeafTradeCodes = (selection: StringIdFilter): string[] =>
  Object.values(selection)
    .filter(node => node.selected === 1 && (node.childIds?.length ?? 0) === 0)
    .map(node => node.id);

/**
 * Trade names for the filename's trade segment: a fully-selected parent group collapses to
 * its own name instead of listing every child (e.g. a fully-selected "Core & Shell" reads as just
 * "Core & Shell", not each of its leaves), recursively up the tree.
 */
const collapseSelectedTradeNames = (
  nodes: BreakdownNode<ApiTrade>[],
  selectedTradeCodes: Set<string>,
  selectableLeavesByCode: Record<string, string[]>
): string[] =>
  nodes.reduce<string[]>((names, node) => {
    const code = node.id as string;
    if (!node.children?.length) {
      if (selectedTradeCodes.has(code)) {
        names.push(node.title || code);
      }
      return names;
    }
    const leaves = selectableLeavesByCode[code] ?? [];
    const fullySelected = leaves.length > 0 && leaves.every(leaf => selectedTradeCodes.has(leaf));
    if (fullySelected) {
      names.push(node.title || code);
    } else {
      names.push(...collapseSelectedTradeNames(node.children, selectedTradeCodes, selectableLeavesByCode));
    }
    return names;
  }, []);

// Scan dates are what happened on the job site, so every user should see the same date regardless of
// where they are — hence formatUTC. They arrive as calendar dates stored at midnight UTC, so formatting
// in a local zone renders the previous day everywhere west of UTC.
const scanDateFormatter = DateConverter.getDateFormatter("MMMM D, YYYY");

function deviationMagnitudesHaveAnyDeviation(magnitudesByFloorAndTrade: { [k: string]: { [k: string]: number } }) {
  if (magnitudesByFloorAndTrade == null) {
    return false;
  }

  const floorIdsWithDeviations = Object.keys(magnitudesByFloorAndTrade);
  for (let i = 0; i < floorIdsWithDeviations.length; i++) {
    const magnitudesByTrade = magnitudesByFloorAndTrade[floorIdsWithDeviations[i]];
    const tradeCodesWithDeviations = Object.keys(magnitudesByTrade);
    for (let j = 0; j < tradeCodesWithDeviations.length; j++) {
      const magnitude = magnitudesByTrade[tradeCodesWithDeviations[j]];
      if (magnitude >= SMALLEST_DEVIATION_IN_METERS) {
        return true;
      }
    }
  }

  return false;
}

const ExportDeviationsIfcWizard: FunctionComponent<Props> = (props) => {
  const { open, projectId, projectName, variant = "deviations", tradeFilterEnabled = true, onClose, onExport } = props;
  const isAsBuilt = variant === "asBuilt";
  const { data: floorsByFirebaseId } = useFloorsForProject(projectId);
  const { data: tradesByCode, isInitialLoading: tradesLoading } = useTrades(projectId);
  const { ifcExportNoDeviationsWarnings } = useFlags();
  // Load every area's scans and their files so the review step can name each area's own most recent
  // pointcloud scan. Only fetched while the wizard is open, since both are project-wide lists; the
  // dates themselves are then selected off the store the queries populate.
  const openProjectId = open ? projectId : "";
  useScanDatasetsForProject(openProjectId);
  useScanDatasetFilesForProject(openProjectId);
  const scanDatesByFloorId = useAvvirSelector(getMostRecentScanDatesByFloorId);

  const floors = useMemo(() => _.values(floorsByFirebaseId ?? {}), [floorsByFirebaseId]);
  const { data: allScanMagnitudesByFloorAndTrade, isLoading: allScansLoading } = useFloorTradeDeviationMagnitudes(projectId, open);
  const { data: currentScanMagnitudesByFloorAndTrade, isLoading: currentScanLoading } = useCurrentScanFloorTradeDeviationMagnitudes(projectId, open);
  // Magnitudes can't tell nothing-built from built-at-designed-position: both
  // report zero. So the as-built flow asks for built counts too.
  const { data: builtElementCountsByFloor, isLoading: builtCountsLoading } = useFloorBuiltElementCounts(projectId, open && isAsBuilt);
  // The same blind spot one level down: a trade built at its designed position
  // and a trade never built both report a magnitude of zero.
  const { data: builtElementCountsByFloorAndTrade, isLoading: tradeBuiltCountsLoading } =
    useFloorTradeBuiltElementCounts(projectId, open && isAsBuilt && tradeFilterEnabled);
  // As-built has its own flag, so its warnings aren't gated on the deviation's.
  const showDeviationWarnings = (isAsBuilt || ifcExportNoDeviationsWarnings) && !allScansLoading && !currentScanLoading;
  const showBuiltWarnings = isAsBuilt && !builtCountsLoading;
  const showTradeBuiltWarnings = isAsBuilt && tradeFilterEnabled && !tradeBuiltCountsLoading;

  const [activeIndex, setActiveIndex] = useState(0);
  const [scanScope, setScanScope] = useState<DeviationsIfcType>("all_deviations");
  const [selectedFloorIds, setSelectedFloorIds] = useState<string[]>([]);
  const [toleranceKey, setToleranceKey] = useState<string>(DEFAULT_DEVIATION_TOLERANCE_OPTION.key);
  const [selectedTradeCodes, setSelectedTradeCodes] = useState<string[]>([]);
  // Opt-in (not opt-out) so parent trade groups are collapsed by default without needing to know
  // the trade list up front — a plain empty Set already means "nothing expanded yet".
  const [expandedTradeCodes, setExpandedTradeCodes] = useState<Set<string>>(new Set());
  const [tradeQuery, setTradeQuery] = useState("");
  const [exportState, setExportState] = useState<ExportState>("idle");
  const selectedTolerance: DeviationToleranceOption = toleranceOptionByKey(toleranceKey);
  const selectedToleranceInMeters = selectedTolerance.inches / INCHES_TO_METERS_RATIO;

  const magnitudesByFloorAndTrade = useMemo(() => {
    return scanScope === "all_deviations" ? allScanMagnitudesByFloorAndTrade : currentScanMagnitudesByFloorAndTrade;
  }, [scanScope, allScanMagnitudesByFloorAndTrade, currentScanMagnitudesByFloorAndTrade]);

  // ----- trade breakdown -----
  // The project's trades, auto-sourced from its BIM PBEs and built into a breakdown so the
  // step lists the leaf trades a user can filter on.
  const tradesBreakdown = useMemo(
    () => buildBreakdownFromList<ApiTrade, ApiTrade>(tradesByCode ?? {}, {
      id: "code", parentId: "parentCode", childIds: "childCodes"
    }),
    [tradesByCode]);
  const tradeNodes = useMemo<BreakdownNode<ApiTrade>[]>(() => Object.values(tradesBreakdown.all), [tradesBreakdown]);
  const leafTradeCodes = useMemo(
    () => tradeNodes.filter(node => !node.children?.length).map(node => node.id as string),
    [tradeNodes]);
  const nonLeafTradeCodes = useMemo(
    () => tradeNodes.filter(node => node.children?.length).map(node => node.id as string),
    [tradeNodes]);

  const floorFirebaseIdsWithoutDeviations = useMemo(() => {
    const result: Set<string> = new Set();
    // All floors are enabled if the feature flag is off
    if (!showDeviationWarnings || magnitudesByFloorAndTrade == null) {
      return result;
    }

    floors.forEach((floor) => {
      if (floor == null) {
        return;
      }
      const floorFirebaseId = floor.firebaseId;
      const magnitudesByTrade = magnitudesByFloorAndTrade[floorFirebaseId];
      if (magnitudesByTrade == null) {
        result.add(floorFirebaseId);
      } else {
        let hasDeviation = false;
        for (let i = 0; i < leafTradeCodes.length; i++) {
          const tradeCode = leafTradeCodes[i];
          const magnitude = magnitudesByTrade[tradeCode];
          if (magnitude != null && magnitude >= SMALLEST_DEVIATION_IN_METERS) {
            hasDeviation = true;
            break;
          }
        }

        if (!hasDeviation) {
          result.add(floorFirebaseId);
        }
      }
    });
    return result;
  }, [floors, showDeviationWarnings, leafTradeCodes, magnitudesByFloorAndTrade]);

  // The gateway omits floors with nothing built, so a floor missing from the
  // counts has no built elements to write out.
  const floorFirebaseIdsWithoutBuiltElements = useMemo(() => {
    const result: Set<string> = new Set();
    if (!showBuiltWarnings) {
      return result;
    }

    floors.forEach((floor) => {
      if (floor == null) {
        return;
      }
      if (!(builtElementCountsByFloor?.[floor.firebaseId] > 0)) {
        result.add(floor.firebaseId);
      }
    });
    return result;
  }, [builtElementCountsByFloor, floors, showBuiltWarnings]);

  // As-built includes every built element however far it deviates, so an area
  // is unexportable only when nothing is built there.
  const disabledFloorFirebaseIds = isAsBuilt ? floorFirebaseIdsWithoutBuiltElements : floorFirebaseIdsWithoutDeviations;
  const areaWarningLabel = isAsBuilt ? "No built PBEs" : "No deviated elements";
  // Select All only reaches selectable areas, so it's spent once those are set.
  const selectableFloorCount = floors.filter(floor => !disabledFloorFirebaseIds.has(floor?.firebaseId)).length;

  const disabledTradeCodes = useMemo(() => {
    const result: Set<string> = new Set();

    // Independent of the tolerance on purpose: the threshold decides where a
    // built element lands, not whether it exports, so it cannot gate trades.
    if (isAsBuilt) {
      if (!showTradeBuiltWarnings || selectedFloorIds.length === 0) {
        return result;
      }

      leafTradeCodes.forEach((tradeCode) => {
        const builtInSomeSelectedFloor = selectedFloorIds.some((floorId) => {
          return builtElementCountsByFloorAndTrade?.[floorId]?.[tradeCode] > 0;
        });
        if (!builtInSomeSelectedFloor) {
          result.add(tradeCode);
        }
      });
      return result;
    }

    // All trade codes are enabled if the feature flag is off
    if (!showDeviationWarnings) {
      return result;
    }

    // Nothing selected yet: leave every trade enabled rather than disabling all of them.
    if (selectedFloorIds.length === 0) {
      return result;
    }

    // A trade is exportable when ANY selected area deviates at or above the tolerance. The export spans
    // every selected area, so requiring a deviation in all of them would disable a trade that genuinely
    // deviates somewhere in the selection — and the more areas you pick, the fewer trades would survive.
    leafTradeCodes.forEach((tradeCode) => {
      const deviatesInSomeSelectedFloor = selectedFloorIds.some((floorId) => {
        const magnitude = magnitudesByFloorAndTrade[floorId]?.[tradeCode];
        return magnitude != null && magnitude >= selectedToleranceInMeters;
      });
      if (!deviatesInSomeSelectedFloor) {
        result.add(tradeCode);
      }
    });
    return result;
  }, [isAsBuilt, showTradeBuiltWarnings, builtElementCountsByFloorAndTrade, showDeviationWarnings, leafTradeCodes, magnitudesByFloorAndTrade, selectedFloorIds, selectedToleranceInMeters]);

  const tradeWarningLabel = isAsBuilt ? "No built PBEs" : "No deviated elements";

  const selectableLeafTradeCodes = useMemo(
    () => leafTradeCodes.filter(code => !disabledTradeCodes.has(code)),
    [leafTradeCodes, disabledTradeCodes]);

  // Nothing built in any selected trade. Unlike the tolerance warning this is a
  // dead end: Next stays blocked with no selectable trade left to tick.
  const noSelectableTrades = showTradeBuiltWarnings
    && selectedFloorIds.length > 0
    && leafTradeCodes.length > 0
    && selectableLeafTradeCodes.length === 0;
  // Selectable (non-disabled) leaf codes in each node's subtree, so a group's checkbox reflects
  // only its selectable leaves and can still reach a fully-checked, toggle-off-able state.
  const selectableLeavesByCode = useMemo(
    () => leafCodesByNode(Object.values(tradesBreakdown.breakdown) as BreakdownNode<ApiTrade>[], code => disabledTradeCodes.has(code)),
    [tradesBreakdown, disabledTradeCodes]);
  const isTradeDisabled = useCallback(
    (code: string) => disabledTradeCodes.has(code) || selectableLeavesByCode[code]?.length === 0,
    [disabledTradeCodes, selectableLeavesByCode]);

  // Steps are built, not fixed: as-built has no scan step, and the trade step
  // appears only when the trade filter is enabled.
  const steps = useMemo<{ key: WizardStep, label: string }[]>(() => [
    ...(isAsBuilt ? [] : [{ key: "scan" as WizardStep, label: "Scan" }]),
    { key: "area", label: "Area" },
    { key: "tolerance", label: "Tolerance" },
    ...(tradeFilterEnabled ? [{ key: "trade" as WizardStep, label: "Trade" }] : []),
    { key: "review", label: "Review" },
  ], [isAsBuilt, tradeFilterEnabled]);

  const activeStep = steps[activeIndex].key;

  const reset = useCallback(() => {
    setActiveIndex(0);
    setScanScope("all_deviations");
    setSelectedFloorIds([]);
    setToleranceKey(DEFAULT_DEVIATION_TOLERANCE_OPTION.key);
    setSelectedTradeCodes([]);
    setExpandedTradeCodes(new Set());
    setTradeQuery("");
    setExportState("idle");
  }, []);

  const handleClose = useCallback(() => {
    onClose();
    reset();
  }, [onClose, reset]);

  // ----- derived values -----------------------------------------------------
  const selectedFloors = useMemo(
    () => floors.filter(floor => selectedFloorIds.includes(floor.firebaseId)),
    [floors, selectedFloorIds]);

  // Selected trade titles in tree order, for the review summary.
  const selectedTradeTitles = useMemo(
    () => leafTradeCodes
      .filter(code => selectedTradeCodes.includes(code))
      .map(code => tradesBreakdown.all[code]?.title || code),
    [leafTradeCodes, selectedTradeCodes, tradesBreakdown]);

  // The pipeline `type` this wizard requests: the as-built variant always exports as-built, while
  // the deviation variant takes whatever the scan step selected.
  const exportIfcType: ExportIfcType = isAsBuilt ? "as_built" : scanScope;

  // The filename tokens below are what the pipeline itself will use: the tolerance and trade labels
  // ride along with the export request (the pipeline can't derive them), while the project name and
  // area are resolved pipeline-side from the same records this preview reads.
  const exportTypeToken = exportTypeFileNameToken(exportIfcType);
  const scanScopeToken = scanScopeFileNameToken(exportIfcType);
  const toleranceToken = toleranceFileNameSlug(selectedTolerance);

  // A project-wide trade selection collapses to a single "AllTrades" token rather than every root
  // trade name; otherwise a fully-selected parent group collapses to its own name (see
  // collapseSelectedTradeNames above).
  const tradesToken = useMemo(() => {
    if (!tradeFilterEnabled || selectedTradeCodes.length === 0) {
      return "";
    }
    if (selectedTradeCodes.length === selectableLeafTradeCodes.length) {
      return tradesFileNameSegment([], true);
    }
    const rootNodes = Object.values(tradesBreakdown.breakdown) as BreakdownNode<ApiTrade>[];
    const selectedSet = new Set(selectedTradeCodes);
    return tradesFileNameSegment(collapseSelectedTradeNames(rootNodes, selectedSet, selectableLeavesByCode), false);
  }, [tradeFilterEnabled, selectedTradeCodes, selectableLeafTradeCodes, tradesBreakdown, selectableLeavesByCode]);

  const zipFileName = generateZipFileName({ projectName, exportType: exportTypeToken });
  const previewFileNames = selectedFloors.map(floor => generateIfcFileName({
    projectName,
    exportType: exportTypeToken,
    scanScope: scanScopeToken,
    area: floor.floorNumber,
    deviationTolerance: toleranceToken,
    trades: tradesToken,
  }));
  const outputSummary = selectedFloors.length === 1
                        ? "Downloads as a single .ifc file."
                        : `${previewFileNames.length} files (one per area) bundled as ${zipFileName}`;

  const doesSelectedToleranceHaveDeviations = useMemo(() => {
    if (!showDeviationWarnings) {
      return true;
    }

    return selectedFloorIds.some((floorId) => {
      return Object.values(magnitudesByFloorAndTrade[floorId] ?? {}).some((magnitude) => {
        return magnitude >= selectedToleranceInMeters;
      });
    });
  }, [magnitudesByFloorAndTrade, selectedFloorIds, selectedToleranceInMeters, showDeviationWarnings]);

  // ----- navigation ---------------------------------------------------------
  const canAdvance = useMemo(() => {
    if (activeStep === "area") {
      return selectedFloorIds.length > 0;
    }
    if (activeStep === "trade") {
      return selectedTradeCodes.length > 0;
    }
    if (activeStep === "tolerance") {
      // A dead end for the deviation export, but not for as-built: every built
      // element still exports, at its as-designed position.
      return isAsBuilt || doesSelectedToleranceHaveDeviations;
    }
    return true;
  }, [activeStep, doesSelectedToleranceHaveDeviations, isAsBuilt, selectedFloorIds.length, selectedTradeCodes.length]);

  const goBack = () => setActiveIndex(index => Math.max(0, index - 1));
  const goNext = () => setActiveIndex(index => Math.min(steps.length - 1, index + 1));

  const runExport = useCallback(() => {
    setExportState("loading");
    const minimumDeviationMetres = isDefaultTolerance(selectedTolerance)
                                   ? undefined
                                   : toleranceOptionToMetres(selectedTolerance);
    // The tolerance and trade tokens go with the request so the pipeline writes the same filenames
    // this wizard just previewed; everything else in the name it derives from the mode and the
    // project/floor records.
    return onExport(exportIfcType, selectedFloorIds, minimumDeviationMetres, tradeFilterEnabled ? selectedTradeCodes : [],
                    { toleranceLabel: toleranceToken, tradesLabel: tradesToken })
      .then(() => { handleClose(); })
      .catch(() => { setExportState("error"); });
  }, [onExport, exportIfcType, selectedFloorIds, selectedTradeCodes, selectedTolerance, tradeFilterEnabled,
      toleranceToken, tradesToken, handleClose]);

  // ----- area helpers -------------------------------------------------------
  const toggleFloor = (firebaseId: string) => {
    setSelectedFloorIds(current => current.includes(firebaseId)
                                   ? current.filter(id => id !== firebaseId)
                                   : [...current, firebaseId]);
  };
  const selectAllFloors = useCallback(() => {
    setSelectedFloorIds(floors
      .map(floor => floor.firebaseId)
      .filter(firebaseId => !disabledFloorFirebaseIds.has(firebaseId))
    );
  }, [disabledFloorFirebaseIds, floors]);
  const deselectAllFloors = () => setSelectedFloorIds([]);

  // ----- trade selection ----------------------------------------------------
  const tradeSelection = useMemo<StringIdFilter>(
    () => updateFilterFromIdList(selectedTradeCodes, convertBreakdownToFilter(tradesBreakdown) as unknown as StringIdFilter),
    [selectedTradeCodes, tradesBreakdown]);

  const setTradesFromSelection = useCallback((next: StringIdFilter) => {
    setSelectedTradeCodes(selectedLeafTradeCodes(next).filter(code => !disabledTradeCodes.has(code)));
  }, [disabledTradeCodes]);
  // Selecting a parent cascades to its leaves; deselecting cascades them off.
  const toggleTrade = useCallback((code: string, on: boolean) => {
    setTradesFromSelection(on
                           ? toggleElement(code, 1, tradeSelection)
                           : toggleElementCascade(code, 0, tradeSelection));
  }, [tradeSelection, setTradesFromSelection]);
  const selectAllTrades = useCallback(() => setSelectedTradeCodes(selectableLeafTradeCodes), [selectableLeafTradeCodes]);
  const deselectAllTrades = useCallback(() => setSelectedTradeCodes([]), []);
  const selectedTradeSet = useMemo(() => new Set(selectedTradeCodes), [selectedTradeCodes]);

  // Trade rows shown for the current search query (empty query shows the full tree).
  const isSearchingTrades = tradeQuery.trim().length > 0;
  const visibleTradeNodes = useMemo(() => {
    const rootNodes = Object.values(tradesBreakdown.breakdown) as BreakdownNode<ApiTrade>[];
    const query = tradeQuery.trim().toLowerCase();
    return query ? filterTradeNodesByQuery(rootNodes, query) : rootNodes;
  }, [tradesBreakdown, tradeQuery]);

  // ----- trade collapse/expand ------------------------------------------------
  const toggleTradeExpand = useCallback((code: string) => {
    setExpandedTradeCodes(current => {
      const next = new Set(current);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  }, []);
  const expandAllTrades = useCallback(() => setExpandedTradeCodes(new Set(nonLeafTradeCodes)), [nonLeafTradeCodes]);
  const collapseAllTrades = useCallback(() => setExpandedTradeCodes(new Set()), []);

  // ----- step bodies --------------------------------------------------------
  const allScansDisabled = useMemo(() => {
    return showDeviationWarnings && !deviationMagnitudesHaveAnyDeviation(allScanMagnitudesByFloorAndTrade);
  }, [showDeviationWarnings, allScanMagnitudesByFloorAndTrade]);

  const currentScanDisabled = useMemo(() => {
    return showDeviationWarnings && !deviationMagnitudesHaveAnyDeviation(currentScanMagnitudesByFloorAndTrade);
  }, [showDeviationWarnings, currentScanMagnitudesByFloorAndTrade]);

  const onScanScopeChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setScanScope(event.target.value as DeviationsIfcType);
    setSelectedFloorIds([]);
    setToleranceKey(DEFAULT_DEVIATION_TOLERANCE_OPTION.key);
    setSelectedTradeCodes([]);
    setExpandedTradeCodes(new Set());
    setTradeQuery("");
    setExportState("idle");
  }, []);

  const scanStep = (
    <div className="ExportDeviationsIfcWizard-step">
      <div className="ExportDeviationsIfcWizard-stepIntro">
        <Typography variant="titleSmall" className="ExportDeviationsIfcWizard-stepTitle">Scan date range</Typography>
        <Typography variant="bodySmall" className="ExportDeviationsIfcWizard-stepSubtitle">
          Select which scans to include in the IFC export.
        </Typography>
      </div>
      <RadioGroup
        className="ExportDeviationsIfcWizard-scanOptions"
        name="scanScope"
        value={scanScope}
        onChange={onScanScopeChange}
      >
        <label className={`ExportDeviationsIfcWizard-scanCard${scanScope === "all_deviations" ? " ExportDeviationsIfcWizard-scanCard--selected" : ""}`}>
          <Radio
            className="ExportDeviationsIfcWizard-allScans" value="all_deviations"
            disabled={allScansDisabled}
          />
          <span className="ExportDeviationsIfcWizard-scanCardText">
            <Typography variant="bodyMedium" className="ExportDeviationsIfcWizard-scanCardTitle">
              {allScansDisabled ? <Tag
                className="ExportDeviationsIfcWizard-scanCardWarning"
                variant="warning"
                intensity="subtle"
                label="No deviated PBEs"
              /> : null}
              All scans to date
            </Typography>
            <Typography variant="bodySmall" className="ExportDeviationsIfcWizard-scanCardDescription">Includes deviations from every scan captured for this project</Typography>
          </span>
        </label>
        <label className={`ExportDeviationsIfcWizard-scanCard${scanScope === "current_deviations" ? " ExportDeviationsIfcWizard-scanCard--selected" : ""}`}>
          <Radio className="ExportDeviationsIfcWizard-currentScan" value="current_deviations" disabled={currentScanDisabled}/>
          <span className="ExportDeviationsIfcWizard-scanCardText">
            {/* No date here: each selected area can have its own most recent scan, so the dates live on the review step. */}
            <Typography variant="bodyMedium" className="ExportDeviationsIfcWizard-scanCardTitle">
              {currentScanDisabled ? <Tag
                className="ExportDeviationsIfcWizard-scanCardWarning"
                variant="warning"
                intensity="subtle"
                label="No deviated PBEs"
              /> : null}
              Most recent scan
            </Typography>
            <Typography variant="bodySmall" className="ExportDeviationsIfcWizard-scanCardDescription">Uses the latest scan available for each selected area</Typography>
          </span>
        </label>
      </RadioGroup>
    </div>
  );

  const areaStep = (
    <div className="ExportDeviationsIfcWizard-step">
      <div className="ExportDeviationsIfcWizard-listHeader">
        <Typography variant="titleSmall" className="ExportDeviationsIfcWizard-stepTitle">Select areas</Typography>
        <div className="ExportDeviationsIfcWizard-listActions">
          <Button
            variant="text"
            className="ExportDeviationsIfcWizard-listAction ExportDeviationsIfcWizard-selectAll"
            onClick={selectAllFloors}
            disabled={selectableFloorCount === 0 || selectedFloorIds.length === selectableFloorCount}
          >Select All</Button>
          <span className="ExportDeviationsIfcWizard-actionSeparator">|</span>
          <Button variant="text" className="ExportDeviationsIfcWizard-listAction ExportDeviationsIfcWizard-deselectAll" onClick={deselectAllFloors} disabled={selectedFloorIds.length === 0}>Deselect All</Button>
        </div>
      </div>
      <div className="ExportDeviationsIfcWizard-list">
        {floors.map(floor => {
          const checked = selectedFloorIds.includes(floor.firebaseId);
          const isDisabled = disabledFloorFirebaseIds.has(floor.firebaseId);
          return (
            <label
              key={floor.firebaseId}
              className={`ExportDeviationsIfcWizard-row${checked ? " ExportDeviationsIfcWizard-row--selected" : ""}${isDisabled ? " ExportDeviationsIfcWizard-row--disabled" : ""}`}
            >
              <Checkbox
                className="ExportDeviationsIfcWizard-areaOption"
                checked={checked}
                disabled={isDisabled}
                onChange={() => toggleFloor(floor.firebaseId)}
              />
              <span className="ExportDeviationsIfcWizard-rowLabel">{floor.floorNumber}</span>
              {isDisabled && (
                <Tag
                  className="ExportDeviationsIfcWizard-noDeviations"
                  variant="warning"
                  intensity="subtle"
                  label={areaWarningLabel}
                />
              )}
            </label>
          );
        })}
      </div>
      <div className="ExportDeviationsIfcWizard-countTag">
        <Tag
          variant="info"
          intensity="subtle"
          label={`${selectedFloorIds.length} of ${floors.length} areas selected`}
        />
      </div>
    </div>
  );

  const toleranceStep = (
    <div className="ExportDeviationsIfcWizard-step">
      <div className="ExportDeviationsIfcWizard-stepIntro">
        <Typography variant="titleSmall" className="ExportDeviationsIfcWizard-stepTitle">Deviation tolerance</Typography>
        <Typography variant="bodySmall" className="ExportDeviationsIfcWizard-stepSubtitle">
          Export includes all deviations at or above the selected threshold.
        </Typography>
      </div>
      <SegmentedButtonGroup
        exclusive
        className="ExportDeviationsIfcWizard-tolerances"
        value={toleranceKey}
        onChange={(_event, value) => { if (value) { setToleranceKey(value); } }}
      >
        {DEVIATION_TOLERANCE_OPTIONS.map(option => (
          <SegmentedButton
            key={option.key}
            className={`ExportDeviationsIfcWizard-tolerance-${option.key}`}
            value={option.key}
          >
            {toleranceDisplayLabel(option)}
          </SegmentedButton>
        ))}
      </SegmentedButtonGroup>
      {doesSelectedToleranceHaveDeviations ?
       <BannerInline
         color="info"
         intensity="subtle"
         supportText={isAsBuilt ? asBuiltToleranceAffirmationText(selectedTolerance) : toleranceAffirmationText(selectedTolerance)}
       /> :
       <BannerInline
         color="warning"
         icon={<Icon family="material" name="warning" size="x-large"/>}
         intensity="subtle"
         supportText={isAsBuilt ? asBuiltToleranceNoDeviationsText(selectedTolerance) : toleranceNoDeviationsText(selectedTolerance)}
       />}
    </div>
  );

  const tradeStep = (
    <div className="ExportDeviationsIfcWizard-step">
      <div className="ExportDeviationsIfcWizard-listHeader">
        <Typography variant="titleSmall" className="ExportDeviationsIfcWizard-stepTitle">Select trades</Typography>
        <div className="ExportDeviationsIfcWizard-listActions">
          <Button
            variant="text"
            className="ExportDeviationsIfcWizard-listAction ExportDeviationsIfcWizard-expandAll"
            onClick={expandAllTrades}
            disabled={nonLeafTradeCodes.length === 0 || expandedTradeCodes.size === nonLeafTradeCodes.length}
          >Expand All</Button>
          <span className="ExportDeviationsIfcWizard-actionSeparator">|</span>
          <Button
            variant="text"
            className="ExportDeviationsIfcWizard-listAction ExportDeviationsIfcWizard-collapseAll"
            onClick={collapseAllTrades}
            disabled={nonLeafTradeCodes.length === 0 || expandedTradeCodes.size === 0}
          >Collapse All</Button>
          <span className="ExportDeviationsIfcWizard-actionSeparator">|</span>
          <Button
            variant="text"
            className="ExportDeviationsIfcWizard-listAction ExportDeviationsIfcWizard-selectAll"
            onClick={selectAllTrades}
            disabled={selectableLeafTradeCodes.length === 0 || selectedTradeCodes.length === selectableLeafTradeCodes.length}
          >Select All</Button>
          <span className="ExportDeviationsIfcWizard-actionSeparator">|</span>
          <Button variant="text" className="ExportDeviationsIfcWizard-listAction ExportDeviationsIfcWizard-deselectAll" onClick={deselectAllTrades} disabled={selectedTradeCodes.length === 0}>Deselect All</Button>
        </div>
      </div>
      {tradesLoading ? (
        <div className="ExportDeviationsIfcWizard-tradeLoading">
          <CircularProgress size={24}/>
        </div>
      ) : leafTradeCodes.length === 0 ? (
        <div className="ExportDeviationsIfcWizard-tradeEmpty">
          <Typography variant="bodySmall">No trades found for this project.</Typography>
        </div>
      ) : (
            <>
              <div className="ExportDeviationsIfcWizard-tradeSearch">
                <Search
                  className="ExportDeviationsIfcWizard-tradeSearchInput"
                  placeholder="Search trades or codes"
                  startDecorator={<Icon family="material" name="search"/>}
                  value={tradeQuery}
                  onChange={event => setTradeQuery(event.target.value)}
                />
              </div>
              <div className="ExportDeviationsIfcWizard-list ExportDeviationsIfcWizard-list--trades">
              {visibleTradeNodes.length === 0 ? (
                <div className="ExportDeviationsIfcWizard-tradeSearchEmpty">
                  <Typography variant="bodySmall" className="ExportDeviationsIfcWizard-tradeSearchEmptyText">
                    No results found. Try a different name or code
                  </Typography>
                </div>
              ) : <TradeTreeRows
                nodes={visibleTradeNodes}
                leavesByCode={selectableLeavesByCode}
                selectedCodes={selectedTradeSet}
                expandedCodes={expandedTradeCodes}
                isSearching={isSearchingTrades}
                onToggle={toggleTrade}
                onToggleExpand={toggleTradeExpand}
                isDisabled={isTradeDisabled}
                renderAdornment={node => isTradeDisabled(node.id as string)
                                         && (!node.children?.length || (!isSearchingTrades && !expandedTradeCodes.has(node.id as string))) ? <Tag
                  className="ExportDeviationsIfcWizard-noDeviations"
                  variant="warning"
                  intensity="subtle"
                  label={tradeWarningLabel}
                /> : null}
              />}
              </div>
            </>
          )}
      {noSelectableTrades && (
        <BannerInline
          className="ExportDeviationsIfcWizard-noBuiltTrades"
          color="warning"
          icon={<Icon family="material" name="warning" size="x-large"/>}
          intensity="subtle"
          supportText={asBuiltNoBuiltTradesText}
        />
      )}
      <div className="ExportDeviationsIfcWizard-countTag">
        <Tag
          variant="info"
          intensity="subtle"
          label={`${selectedTradeCodes.length} ${selectedTradeCodes.length === 1 ? "trade" : "trades"} selected`}
        />
      </div>
    </div>
  );

  const reviewStep = (
    <div className="ExportDeviationsIfcWizard-step">
      <Typography variant="titleSmall" className="ExportDeviationsIfcWizard-stepTitle">Review your export settings</Typography>
      <div className="ExportDeviationsIfcWizard-reviewTable">
        {/* The as-built export has no scan choice to review — it always uses each area's most recent scan. */}
        {!isAsBuilt && (
          <div className="ExportDeviationsIfcWizard-reviewRow">
            <span className="ExportDeviationsIfcWizard-reviewLabel">Scan</span>
            <span className="ExportDeviationsIfcWizard-reviewValue">
              {scanScope === "all_deviations" ? "All scans to date" : "Most recent scan"}
            </span>
          </div>
        )}
        <div className="ExportDeviationsIfcWizard-reviewRow">
          <span className="ExportDeviationsIfcWizard-reviewLabel">Area</span>
          <span className="ExportDeviationsIfcWizard-reviewValue">
            {selectedFloors.map(floor => {
              const scanDate = scanDatesByFloorId[floor.firebaseId];
              return (
                <span key={floor.firebaseId} className="ExportDeviationsIfcWizard-reviewArea">
                  <span className="ExportDeviationsIfcWizard-reviewAreaName">{floor.floorNumber}</span>
                  {/*
                    Named per area because each one can have its own most recent scan. The as-built export
                    always uses that scan, and the deviation export only when its scope is the current scan
                    — "all scans" spans every date, so no single date describes it.
                  */}
                  {(isAsBuilt || scanScope === "current_deviations") && (
                    <span className="ExportDeviationsIfcWizard-reviewAreaScanDate">
                      most recent scan: {scanDate ? scanDateFormatter.formatUTC(scanDate) : "not available"}
                    </span>
                  )}
                </span>
              );
            })}
          </span>
        </div>
        <div className="ExportDeviationsIfcWizard-reviewRow">
          <span className="ExportDeviationsIfcWizard-reviewLabel">Tolerance</span>
          <span className="ExportDeviationsIfcWizard-reviewValue">{toleranceSummaryText(selectedTolerance)}</span>
        </div>
        {tradeFilterEnabled && (
          <div className="ExportDeviationsIfcWizard-reviewRow">
            <span className="ExportDeviationsIfcWizard-reviewLabel">Trades</span>
            <span className="ExportDeviationsIfcWizard-reviewValue">
              {selectedTradeTitles.join(", ")} ({selectedTradeTitles.length} selected)
            </span>
          </div>
        )}
      </div>
      <div className="ExportDeviationsIfcWizard-outputBox">
        <Typography variant="bodyMedium" className="ExportDeviationsIfcWizard-outputTitle">Output files</Typography>
        <ul className="ExportDeviationsIfcWizard-outputList">
          {previewFileNames.map(name => <li key={name}>{name}</li>)}
        </ul>
        <Typography variant="bodySmall" className="ExportDeviationsIfcWizard-outputSummary">
          {outputSummary}
        </Typography>
      </div>
      {exportState === "error" && (
        <div className="ExportDeviationsIfcWizard-warning">
          <Icon family="material" name="error"/>
          <Typography variant="bodySmall">Something went wrong starting your export. Please try again.</Typography>
        </div>
      )}
    </div>
  );

  const stepBodyByKey: Record<WizardStep, React.ReactNode> = {
    scan: scanStep,
    area: areaStep,
    tolerance: toleranceStep,
    trade: tradeStep,
    review: reviewStep,
  };

  // ----- footer -------------------------------------------------------------
  const footer = (
    <div className="ExportDeviationsIfcWizard-actions">
      <Button variant="text" className="ExportDeviationsIfcWizard-cancel" onClick={handleClose} disabled={exportState === "loading"}>
        Cancel
      </Button>
      <div className="ExportDeviationsIfcWizard-actionsRight">
        {activeIndex > 0 && (
          <Button variant="outlined" className="ExportDeviationsIfcWizard-back" onClick={goBack} disabled={exportState === "loading"}>
            Back
          </Button>
        )}
        {activeStep === "review" ? (
          <Button
            className="ExportDeviationsIfcWizard-export"
            onClick={runExport}
            disabled={exportState === "loading" || selectedFloorIds.length === 0 || (tradeFilterEnabled && selectedTradeCodes.length === 0)}
          >
            {exportState === "loading" ? <CircularProgress size={20}/> : (exportState === "error" ? "Retry" : "Export")}
          </Button>
        ) : (
           <Button
             className="ExportDeviationsIfcWizard-next"
             onClick={goNext}
             disabled={!canAdvance}
           >
             Next
           </Button>
         )}
      </div>
    </div>
  );

  return (
    <ThemeProvider theme={NovaTheme} defaultMode="light" storageManager={null}>
      <Modal open={open} onClose={handleClose}>
        <Paper
          className="ExportDeviationsIfcWizard"
          role="dialog"
          aria-label={isAsBuilt ? "Export As-Built Dialog" : "Export Deviations Dialog"}
        >
          <div className="ExportDeviationsIfcWizard-header">
            <div>
              <Typography variant="titleMedium" className="ExportDeviationsIfcWizard-title">{isAsBuilt ? "Export As-Built (.ifc)" : "Export Deviations (.ifc)"}</Typography>
              <Typography variant="bodySmall" className="ExportDeviationsIfcWizard-subtitle">Configure filters for your IFC export</Typography>
            </div>
          </div>
          <Divider/>
          <div className="ExportDeviationsIfcWizard-stepper">
            <Stepper>
              {steps.map((step, index) => {
                const completed = index < activeIndex;
                return (
                  <Step
                    key={step.key}
                    active={index === activeIndex}
                    completed={completed}
                    icon={completed ? <Icon family="material" name="check" size={16}/> : index + 1}
                  >
                    {step.label}
                  </Step>
                );
              })}
            </Stepper>
          </div>
          <div className="ExportDeviationsIfcWizard-content">
            {stepBodyByKey[activeStep]}
          </div>
          <Divider/>
          {footer}
        </Paper>
      </Modal>
    </ThemeProvider>
  );
};

export default ExportDeviationsIfcWizard;
